import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountService,
  ConfigService,
  FeaturesService,
  LanguageService,
  MetaService,
  PlatformService,
  ProfileService,
} from '@app/core/services';
import { LibrarySidebarService } from '@app/library/sidebars/library-sidebar/library-sidebar.service';
import { LibraryRouter } from '@app/models/library.router';
import { CLibraryFilterData } from '@app/store/models';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { PanesService } from '@core/services/panes.service';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadFilteredLanguages, loadProfileSettings, loadSubjects } from '@store/actions/data.actions';
import { loadMe, loadMyBillingPlan } from '@store/actions/profile.actions';
import { getUserServiceCategories } from '@store/reducers/content.reducer';
import { getAccountCheck, getLanguages } from '@store/reducers/data.reducer';
import { getMe, getMyBillingPlan, getMyProfile } from '@store/reducers/profile.reducer';
import {
  AccountPlan,
  BillingSubscriptionStatusEnum,
  EContentModeType,
  EContentPaneFormat,
  EContentPanelRoute,
  FindContentStrategyEnum,
  IAccountCheck,
  IContentPane,
  IImageFile,
  Language,
  Subject as BaseSubject,
  SubjectCategoryEnum,
  User,
  UserAchievementEnum,
  UserProfile,
  UserSegmentEnum,
  UserServiceCategory,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, filter, of, takeUntil } from 'rxjs';

@Component({
  selector: 'app-library-header-new',
  templateUrl: './library-header-new.component.html',
  styleUrls: ['./library-header-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryHeaderNewComponent extends ChangableComponent implements OnInit, OnDestroy {
  public hasSegment = AccountService.hasSegment;
  public hasAchievement = AccountService.hasAchievement;
  public term = '';
  public expandSearch = false;
  public imageUrl: string;
  public titles = [];
  public baseTitle = null;
  public metaImage: IImageFile;
  public panesList: IContentPane[];
  public scrollThreshold = 0;
  public allSteps = true;
  public checkedStepsCount: number;
  public checkedOrSkippedStepsCount = 0;
  public maxSteps = 6;
  public isPanes: boolean;
  public meetingFormOpen: boolean;
  public schoolFormOpen: boolean;
  public isFocus: boolean;
  public panesLoading: boolean;
  public plan: AccountPlan;
  public openResumePlanModal: boolean;

  private contentFilter: CLibraryFilterData;
  private libRouter = new LibraryRouter();
  private subjects: BaseSubject[] = [];
  private _userServiceCategories: UserServiceCategory[];
  private defaultImageUrl = this.assetsUrl('/assets/images/headers/lessons.jpg');
  private _currentLanguage: Language;
  private _languages: Language[];
  private _me: User;
  private _profile: UserProfile;
  private _accountCheck: Partial<IAccountCheck>;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    public panesService: PanesService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly meta: MetaService,
    private readonly configService: ConfigService,
    private readonly translate: TranslateService,
    private readonly sidebarService: LibrarySidebarService,
    private readonly languageService: LanguageService,
    private readonly features: FeaturesService,
    protected readonly cdr: ChangeDetectorRef,
    protected platform: PlatformService,
    protected profileService: ProfileService,
  ) {
    super(cdr, platform);
    this.imageUrl = this.defaultImageUrl;
    this.sidebarService.enableFilter();
  }

  ngOnInit(): void {
    this.panesService.panesLoading$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (loading) => {
        this.panesLoading = loading;
        this.prepare();
        this.prepareCategories();
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.panesLoading = false;
      },
    });

    combineLatest([
      this.store.select(getLibraryRouteData).pipe(filter((v) => !!v)),
      this.configService.subjects$.pipe(filter((v) => !!v)),
      this.store.select(getUserServiceCategories).pipe(filter((v) => !!v)),
      this.languageService.language$.pipe(filter((v) => !!v)),
      this.store.select(getLanguages).pipe(filter((v) => !!v)),
      this.store.select(getMe).pipe(filter((v) => !!v)),
      this.store.select(getMyProfile).pipe(filter((v) => !!v)),
      this.store.select(getAccountCheck).pipe(filter((v) => !!v)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([
          contentFilter,
          subjects,
          userServiceCategories,
          currentLanguage,
          languages,
          me,
          profile,
          accountCheck,
        ]) => {
          this.contentFilter = contentFilter;
          this.subjects = subjects;
          this._userServiceCategories = userServiceCategories;
          this._currentLanguage = currentLanguage;
          this._languages = languages.filtered;
          this._me = me;
          this._profile = profile;
          this._accountCheck = accountCheck;
          this.allSteps = this.setAllSteps();
          this.prepare();
          this.updateMeta();

          this.libRouter.setBaseRoute(this.contentFilter.baseRoute);
          this.libRouter.setContentMode(this.contentFilter.contentMode);
          this.libRouter.setContentTypes(this.contentFilter.contentTypeCodes);
          this.libRouter.setSubjects(this.contentFilter.subjectCodes);
          // Temporary hide for classes https://app.clickup.com/t/85zrxkyav
          if (this.contentFilter.baseRoute !== EContentPanelRoute.classes) {
            this.prepareCategories();
          } else {
            this.panesList = [];
          }
          this.detectChanges();
          return of(me);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
          this.store.dispatch(loadSubjects({ locale: this._me.ui_language, force: true }));
          this.store.dispatch(loadFilteredLanguages({ locale: this._me.ui_language }));
          this.store.dispatch(loadProfileSettings());
        },
      });

    this.store
      .select(getMyBillingPlan)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (plan) => {
          this.plan = plan;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMyBillingPlan());
        },
      });

    this.expandSearch = this.term?.length > 0;
  }

  private prepareCategories() {
    if (this.panesLoading) {
      if (this.panesService.oldCategories) {
        this.panesList = this.panesService.oldCategories;
      }
    } else {
      if (
        this.contentFilter &&
        this.contentFilter.baseRoute &&
        this.contentFilter.baseRoute !== EContentPanelRoute.classes
      ) {
        this.panesList = this.setPaneList();
      }
    }
  }

  public afterScrollThreshold() {
    if (this.deviceService.isDesktop()) {
      return 112;
    }
    return 60;
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  public get isTutorsPage(): boolean {
    return this.contentFilter.baseRoute === EContentPanelRoute.tutors;
  }

  public get isProfileWidgetVisible(): boolean {
    const pageArray = [
      EContentPanelRoute.main,
      EContentPanelRoute.tutors,
      EContentPanelRoute.lessons,
      EContentPanelRoute.classes,
    ];
    return (
      pageArray.indexOf(this.contentFilter.baseRoute) !== -1 && !this.allSteps && this.checkedOrSkippedStepsCount < 6
    );
  }

  private setAllSteps(): boolean {
    const segments = [UserSegmentEnum.has_approved_bank_card, UserSegmentEnum.use_mobile_app];

    const achievements = [UserAchievementEnum.added_working_time_once, UserAchievementEnum.created_user_services_once];

    const filteredSegments = this._me?.segments
      .filter((_a) => segments.includes(_a.segment))
      .sort((a, b) => {
        const _a = new Date(a.created_at);
        const _b = new Date(b.created_at);
        return _b > _a ? 1 : -1;
      });

    const filteredAchievements = this._me?.achievements
      .filter((_a) => achievements.includes(_a.achievement))
      .sort((a, b) => {
        const _a = new Date(a.created_at);
        const _b = new Date(b.created_at);
        return _b > _a ? 1 : -1;
      });

    const teachingSubjects = this._profile?.teaching_subjects?.length > 0 ? 1 : 0;

    const isTeacherAllStepsFilled = (
      this.isTeacher
        ? AccountService.isAllStepsFilledProfileTeacher(this._accountCheck)
        : AccountService.isAllStepsFilledProfileStudent(this._accountCheck)
    )
      ? 1
      : 0;
    const isServiceUseOrSkip =
      this.hasSegment(this._me, UserSegmentEnum.skipped_services_segment_in_introducion_form) ||
      this.hasAchievement(this._me, UserAchievementEnum.created_user_services_once)
        ? 1
        : 0;
    const isMobileUseOrSkip =
      this.hasSegment(this._me, UserSegmentEnum.skipped_mobile_app_segment_in_introducion_form) ||
      this.hasSegment(this._me, UserSegmentEnum.use_mobile_app)
        ? 1
        : 0;
    const isCardUseOrSkip =
      this.hasSegment(this._me, UserSegmentEnum.skipped_bank_card_segment_in_introducion_form) ||
      this.hasSegment(this._me, UserSegmentEnum.has_approved_bank_card)
        ? 1
        : 0;
    const isScheduleUseOrSkip =
      this.hasSegment(this._me, UserSegmentEnum.skipped_schedule_segment_in_introducion_form) ||
      filteredAchievements.length > 0
        ? 1
        : 0;

    this.checkedStepsCount =
      filteredSegments.length + filteredAchievements.length + teachingSubjects + isTeacherAllStepsFilled;

    this.checkedOrSkippedStepsCount =
      isTeacherAllStepsFilled +
      teachingSubjects +
      isScheduleUseOrSkip +
      isMobileUseOrSkip +
      isCardUseOrSkip +
      isServiceUseOrSkip;

    return this.checkedStepsCount === this.maxSteps;
  }

  public onCreateTitle(): string {
    switch (this.contentFilter.baseRoute) {
      case EContentPanelRoute.lessons:
        return 'my-library.actions.create-lesson';
      case EContentPanelRoute.tutors:
        return 'library.header.actions.teachers';
      case EContentPanelRoute.classes:
        return 'library.header.actions.study';
      case EContentPanelRoute.collections:
        return 'my-library.actions.create-collection';
      case EContentPanelRoute.schools:
        return 'school-header.actions.create';
      default:
        return null;
    }
  }

  public onCreated() {
    let url = [];
    let openInNewTab = false;
    switch (this.contentFilter.baseRoute) {
      case EContentPanelRoute.lessons:
        url = ['constructor', 'edit', 'new', 'constructor', 'new'];
        openInNewTab = true;
        break;
      case EContentPanelRoute.tutors:
        url = ['me', 'teacher'];
        break;
      case EContentPanelRoute.classes:
        this.onToggleMeetingForm();
        break;
      case EContentPanelRoute.schools:
        this.onToggleSchoolForm();
        break;
      case EContentPanelRoute.collections:
        url = ['content', 'collections', 'edit', 'new', 'constructor', 'new'];
        openInNewTab = true;
        break;
      default:
        url = [];
        break;
    }
    if (openInNewTab) {
      window.open(url.join('/'));
    } else {
      this.router.navigate(url).catch();
    }
  }

  public onFocus() {
    this.isFocus = !this.isFocus;
  }

  public onTermChange() {
    this.libRouter.setTerm(this.term);
    const link = this.libRouter.getLink();
    this.router.navigate([link.path], { queryParams: link.parameters }).then();
    this.detectChanges();
  }

  public onToggleSearchForm() {
    this.expandSearch = !this.expandSearch;
    this.detectChanges();
  }

  public onToggleResumePlan() {
    this.openResumePlanModal = !this.openResumePlanModal;
    this.detectChanges();
  }

  public onToggleMeetingForm() {
    if (this.plan?.subscription_status !== BillingSubscriptionStatusEnum.not_active) {
      this.meetingFormOpen = !this.meetingFormOpen;
    } else {
      this.openResumePlanModal = true;
    }
  }

  public onToggleSchoolForm() {
    this.schoolFormOpen = !this.schoolFormOpen;
    this.detectChanges();
  }

  public clearSearchForm() {
    this.term = '';
    this.onTermChange();
  }

  public loadFinished(pane, e) {
    this.isPanes = e;
    this.scrollThreshold = 0;
  }

  public canSearch(): boolean {
    switch (this.contentFilter?.baseRoute) {
      case EContentPanelRoute.main:
      case EContentPanelRoute['people-news']:
      case EContentPanelRoute['classroom-news']:
      case EContentPanelRoute.comments:
      case EContentPanelRoute['school-news']:
        return false;
      default:
        return true;
    }
  }

  public canBreadcrumbs(): boolean {
    switch (this.contentFilter?.baseRoute) {
      case EContentPanelRoute.main:
      case EContentPanelRoute['people-news']:
      case EContentPanelRoute['classroom-news']:
      case EContentPanelRoute.comments:
      case EContentPanelRoute['school-news']:
        return false;
      default:
        return true;
    }
  }

  private prepare() {
    if (!this.contentFilter) {
      return;
    }

    if (this.panesLoading) {
      if (this.panesService.oldTitles) {
        if (this.panesService.oldTitles.title && this.panesService.oldTitles.title.length) {
          this.titles = this.panesService.oldTitles.title;
        } else if (this.panesService.oldTitles.baseTitle) {
          this.baseTitle = this.panesService.oldTitles.baseTitle;
        }
        return;
      }
    }

    let titles: string[] = [];
    const images = [];
    const metaImages = [];
    if (this.contentFilter?.baseRoute) {
      this.baseTitle = `${this.contentFilter?.baseRoute}.page-header-title`;
    }

    if (this.contentFilter.subjectCodes?.length > 0) {
      for (const ss of this.contentFilter.subjectCodes) {
        const subj = this.subjects.find((s) => s.code === ss);
        if (subj) {
          let type = '';
          if (subj.category_id === SubjectCategoryEnum.foreign_language) {
            type = `<span class="type">${this.translate.instant('subject-filters.foreign-language')}</span>`;
          }
          if (subj.category_id === SubjectCategoryEnum.native_language) {
            type = `<span class="type">${this.translate.instant('subject-filters.native-language')}</span>`;
          }

          const title = subj.title || '';
          if (title) {
            titles.push(titles.length > 0 ? `, ${title} ${type}` : `${title} ${type}`);
          }
          if (subj.banner) {
            images.push(subj.banner);
          }
          if (subj.cover) {
            metaImages.push(subj.cover);
          }
        }
      }
    }
    if (this.contentFilter.contentTypeCodes?.length > 0) {
      if (titles.length > 0) {
        titles.push(' - ');
      }
      const ctTitles: string[] = [];
      let title = [];

      switch (this.contentFilter.baseRoute) {
        case EContentPanelRoute.classes:
          title = this.convertUserServiceCategoriesToTitles(this.contentFilter.contentTypeCodes);
          break;
        default:
          title = LibraryRouter.convertContentTypesToFilter(this.contentFilter.contentTypeCodes).map((_s) =>
            this.translate.instant(_s.title),
          );
      }
      if (title) {
        ctTitles.push(title.join(', '));
      }
      titles = titles.concat(ctTitles);
    }

    if (titles.length > 0) {
      titles = [titles.join('')];
    }
    this.titles = titles;
    if (!this.panesLoading) {
      this.panesService.oldTitles.title = this.titles;
      this.panesService.oldTitles.baseTitle = this.baseTitle;
    }

    // TODO: remove it after added image
    const baseRoute = this.contentFilter?.baseRoute.toString().split('/')[0];
    if (['people-news', 'classroom-news', 'comments', 'school-news'].includes(baseRoute)) {
      this.imageUrl = this.assetsUrl('/assets/images/headers/main.jpg');
    } else {
      this.imageUrl =
        images.length && images[0].lg ? this.assetsUrl(images[0].lg.url) : this.setImageUrl() || this.defaultImageUrl;
    }

    if (metaImages.length && metaImages[0]) {
      this.metaImage = metaImages[0].lgX2 || metaImages[0].lg || metaImages[0].mdX2 || metaImages[0].md;
    } else if (images.length && images[0]) {
      this.metaImage = images[0].lgX2 || images[0].lg || images[0].mdX2 || images[0].md;
    }

    this.term = this.contentFilter.term;
  }

  private setImageUrl(): string {
    const baseRoute = this.contentFilter?.baseRoute.toString().split('/')[0];
    return this.assetsUrl(
      ['/assets/images/headers', this.deviceService.isMobile() ? `${baseRoute}_mobile` : baseRoute].join('/') + '.jpg',
    );
  }

  private setPaneList(): IContentPane[] {
    const subjectIds = this.subjects
      .filter((_s) => !!this.contentFilter?.subjectCodes.find((_sc) => _sc === _s.code))
      .map((_s) => _s.id);
    const languageIds = this._languages
      .filter((_l) => !!this.contentFilter?.languageCodes.find((_lc) => _lc === _l.code))
      .map((_l) => _l.id);

    return [
      {
        format: EContentPaneFormat.categories,
        order: 0,
        loaded: false,
        hidden: false,
        directoriesFilter: {
          baseRoute: this.contentFilter?.baseRoute,
          use: FindContentStrategyEnum.library,
          language_id: languageIds,
          subject_id: subjectIds,
          locale: this._currentLanguage?.code,
        },
        displayMode: 'simple',
      },
    ];
  }

  private updateMeta() {
    const titleParts = [];
    const route = this.contentFilter?.baseRoute || 'lessons';
    switch (this.contentFilter.contentMode) {
      case EContentModeType.all:
        break;

      case EContentModeType.my:
        titleParts.push(`${route}.meta-mine-title`);
        break;

      case EContentModeType.favorites:
        titleParts.push(`${route}.meta-favorites-title`);
        break;

      case EContentModeType.popular:
        titleParts.push(`${route}.meta-popular-title`);
        break;

      case EContentModeType['last-added']:
        titleParts.push(`${route}.meta-recently-created-title`);
        break;

      case EContentModeType['last-viewed']:
        titleParts.push(`${route}.meta-last-viewed-title`);
        break;
    }

    const titleWithoutHtml = [this.stripHtml(this.titles)];
    const metaTitles = [...titleParts, ...titleWithoutHtml, `${route}.meta-title`].filter((s) => s !== '-');

    this.meta.setTitle(metaTitles);
    this.meta.setKeywords(metaTitles);
    this.meta.setDescriptions(metaTitles, '-');
    if (this.metaImage) {
      this.meta.setImage(this.metaImage.url, '', +this.metaImage.width, +this.metaImage.height);
    }

    // TODO other meta
  }

  public stripHtml(html): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private convertUserServiceCategoriesToTitles(userServiceCategories: string[]) {
    return userServiceCategories.map((category) => this._userServiceCategories.find((c) => c.code === category)?.title);
  }

  public trackByFn(index) {
    return index;
  }
}
